import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Clock, Truck, MapPin, Phone, Mail, ChevronRight, Wine, Beer, UtensilsCrossed, ShoppingCart, Star, X } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import AgeGate from '@/components/AgeGate';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_nj-liquor-delivery/artifacts/q14l1b4u_1001594216.jpg";
const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/5f713053-9b7f-4a9e-a9cb-96e4d2fea4ae/images/562588e43adea2cf7701cd2f703f72bb814dcc711deba6c6389d7280350cb4d3.png";

const CATEGORY_META = {
  "Wine": { icon: Wine, image: "https://images.unsplash.com/photo-1527281400683-1aae777175f8?w=400", desc: "Fine red, white & sparkling wines" },
  "Beer & FMB": { icon: Beer, image: "https://images.unsplash.com/photo-1615332579037-3c44b3660b53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxjcmFmdCUyMGJlZXJ8ZW58MHx8fHwxNzc2MzUyNjY5fDA&ixlib=rb-4.1.0&q=85", desc: "Craft beers, ciders & flavored beverages" },
  "Liquor": { icon: Wine, image: "https://images.unsplash.com/photo-1627503607711-3b427348cbd7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxwcmVtaXVtJTIwd2luZSUyMGJvdHRsZXN8ZW58MHx8fHwxNzc2MzUyNjY5fDA&ixlib=rb-4.1.0&q=85", desc: "Premium spirits, whiskeys & vodkas" },
  "Grocery": { icon: ShoppingCart, image: "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400", desc: "Snacks, mixers & daily essentials" },
  "Deli & Hot Food": { icon: UtensilsCrossed, image: "https://static.prod-images.emergentagent.com/jobs/5f713053-9b7f-4a9e-a9cb-96e4d2fea4ae/images/5669083f4dea3d0fb0b2840277adf7f25e2fc6a5a1dd4717b2423bf6a3a4674e.png", desc: "Hot meals, subs & fresh deli platters" }
};

export default function LandingPage() {
  const { itemCount } = useCart();
  const { isAdult, verifyAge } = useAuth();
  const [storeInfo, setStoreInfo] = useState(null);
  const [showAgeGate, setShowAgeGate] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    document.title = "Hoboken Liquor Grocery & Deli — Wine, Beer & Spirits Delivery in Hoboken, NJ";
    const ageStatus = localStorage.getItem('age_status');
    if (!ageStatus) {
      setShowAgeGate(true);
    }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/store-info`).then(r => r.json()).then(setStoreInfo).catch(() => {});
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories`).then(r => r.json()).then(data => {
      setCategories(data.categories || []);
    }).catch(() => {});
  }, []);

  const handleAgeResult = (status) => {
    setShowAgeGate(false);
    verifyAge(status);
  };

  const isOpen = storeInfo?.is_open !== false;

  // Filter categories based on age
  const visibleCategories = categories.filter(cat => {
    if (!isAdult && ["Wine", "Beer & FMB", "Liquor"].includes(cat.name)) {
      return false;
    }
    return cat.active !== false;
  });

  return (
    <div className="min-h-screen bg-[#0B0E14]" data-testid="landing-page">
      {showAgeGate && <AgeGate onResult={handleAgeResult} />}

      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-[#0B0E14]/70 backdrop-blur-xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Hoboken LGD" className="w-10 h-10 rounded-lg object-cover" />
            <span className="font-['Playfair_Display'] text-lg font-medium text-white tracking-tight hidden sm:block">HOBOKEN LIQUOR GROCERY & DELI</span>
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm font-light tracking-wide">
            <a href="#menu" className="text-[#94A3B8] hover:text-white transition-colors">Menu</a>
            <a href="#about" className="text-[#94A3B8] hover:text-white transition-colors">About</a>
            <a href="#contact" className="text-[#94A3B8] hover:text-white transition-colors">Contact</a>
          </div>
          <div className="flex items-center gap-4">
            {isOpen && (
              <span className="hidden sm:flex items-center gap-2 text-xs text-[#10B981] font-medium">
                <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />Open Now
              </span>
            )}
            <Link to="/checkout" className="relative" data-testid="cart-icon">
              <ShoppingBag className="w-5 h-5 text-[#94A3B8] hover:text-white transition-colors" />
              {itemCount > 0 && <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-[#cc3737] text-[10px] text-white flex items-center justify-center font-medium">{itemCount}</span>}
            </Link>
            <Link to="/shop" className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-5 py-2 text-sm font-medium transition-colors" data-testid="order-now-btn">
              Order Now
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-center" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="Store" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14]/95 via-[#0B0E14]/70 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-32">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6 animate-fade-in-up stagger-1">
              <span className="px-3 py-1 rounded-full bg-[#b56b38]/20 text-[#b56b38] text-xs font-medium tracking-wider uppercase">Hoboken, NJ</span>
              {isOpen && <span className="px-3 py-1 rounded-full bg-[#10B981]/20 text-[#10B981] text-xs font-medium">Delivering in ~45 min</span>}
            </div>
            <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white tracking-tighter leading-none mb-6 animate-fade-in-up stagger-2">
              Premium Liquor,<br />Grocery & Deli<br /><span className="text-[#b56b38]">Delivered</span>
            </h1>
            <p className="text-[#94A3B8] text-base sm:text-lg font-light leading-relaxed mb-8 max-w-lg animate-fade-in-up stagger-3">
              Your neighborhood store, now at your door. Spirits, beer, wine, fresh deli, and hot food delivered across Hoboken.
            </p>
            <div className="flex flex-wrap gap-4 animate-fade-in-up stagger-4">
              <Link to="/shop" className="bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-8 py-3 font-medium transition-colors flex items-center gap-2" data-testid="hero-order-btn">
                <Truck className="w-4 h-4" /> Order Delivery
              </Link>
              <a href="#menu" className="border border-white/20 hover:border-white hover:bg-white/5 text-white rounded-full px-8 py-3 font-medium transition-all flex items-center gap-2" data-testid="hero-browse-btn">
                Browse Menu <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 lg:py-32 relative noise-overlay" data-testid="how-it-works">
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-4">How It Works</h2>
          <p className="text-[#94A3B8] font-light mb-16 max-w-md mx-auto">Three simple steps to get your favorites delivered</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { icon: ShoppingBag, title: "Browse Menu", desc: "Explore our wide selection of liquor, beer, wine, grocery, and deli items" },
              { icon: ShoppingCart, title: "Place Order", desc: "Add items to cart, choose delivery time, and checkout securely" },
              { icon: Truck, title: "Fast Delivery", desc: "Our drivers deliver to your door across Hoboken in ~45 minutes" },
            ].map((step, i) => (
              <div key={i} className="bg-[#12151C] border border-white/5 rounded-xl p-8 hover:border-white/15 transition-all duration-300 animate-fade-in-up" style={{ animationDelay: `${i * 0.15}s`, opacity: 0 }}>
                <div className="w-14 h-14 rounded-full bg-[#cc3737]/20 flex items-center justify-center mx-auto mb-6">
                  <step.icon className="w-6 h-6 text-[#cc3737]" />
                </div>
                <span className="text-[#b56b38] text-sm font-medium tracking-wider uppercase mb-2 block">Step {i + 1}</span>
                <h3 className="font-['Playfair_Display'] text-xl font-medium text-white mb-3">{step.title}</h3>
                <p className="text-[#94A3B8] font-light text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section id="menu" className="py-20 lg:py-32" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-4 text-center">Shop by Category</h2>
          <p className="text-[#94A3B8] font-light mb-16 text-center">From premium spirits to hot deli, we have it all</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {visibleCategories.map((cat, i) => {
              const meta = CATEGORY_META[cat.name] || { icon: ShoppingCart, image: cat.image || "https://images.unsplash.com/photo-1527281400683-1aae777175f8?w=400", desc: cat.description || "Premium selections" };
              const IconComp = meta.icon;
              return (
                <Link to={`/shop?category=${encodeURIComponent(cat.name)}`} key={cat.name} className="group relative overflow-hidden rounded-xl aspect-[4/5] animate-fade-in-up" style={{ animationDelay: `${i * 0.1}s`, opacity: 0 }} data-testid={`category-card-${i}`}>
                  <img src={meta.image} alt={cat.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0E14] via-[#0B0E14]/40 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <IconComp className="w-6 h-6 text-[#b56b38] mb-3" />
                    <h3 className="font-['Playfair_Display'] text-xl font-medium text-white mb-1">{cat.name}</h3>
                    <p className="text-[#94A3B8] text-sm font-light">{meta.desc || cat.description}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="py-20 lg:py-32 bg-[#12151C] relative noise-overlay" data-testid="about-section">
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#b56b38] text-sm font-medium tracking-wider uppercase mb-4 block">About Us</span>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-6">Your Neighborhood Store Since Day One</h2>
              <p className="text-[#94A3B8] font-light leading-relaxed mb-6">
                Hoboken Liquor Grocery & Deli has been serving the Hoboken community with premium spirits, fine wines, cold beer, fresh groceries, and delicious deli food. Now we bring it all to your doorstep.
              </p>
              <div className="space-y-4">
                <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-[#cc3737]" /><span className="text-[#94A3B8] font-light">98 Willow Ave, Hoboken, NJ</span></div>
                <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-[#cc3737]" /><span className="text-[#94A3B8] font-light">(201) 555-0198</span></div>
                <div className="flex items-center gap-3"><Mail className="w-5 h-5 text-[#cc3737]" /><span className="text-[#94A3B8] font-light">info@hobokenlgd.com</span></div>
                <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-[#cc3737]" /><span className="text-[#94A3B8] font-light">Mon-Thu 9AM-10PM | Fri-Sat 9AM-11PM | Sun 10AM-9PM</span></div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { num: "500+", label: "Products" },
                { num: "45min", label: "Avg Delivery" },
                { num: "21+", label: "Age Verified" },
                { num: "4.8", label: "Avg Rating", icon: Star },
              ].map((stat, i) => (
                <div key={i} className="bg-[#0B0E14] border border-white/5 rounded-xl p-6 text-center hover:border-white/10 transition-colors">
                  <span className="font-['Playfair_Display'] text-2xl font-medium text-[#b56b38]">{stat.num}</span>
                  <p className="text-[#64748B] text-sm mt-1 font-light">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Delivery Area */}
      <section className="py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-4">We Deliver Across Hoboken</h2>
          <p className="text-[#94A3B8] font-light mb-8">Fast delivery from 98 Willow Ave to anywhere in Hoboken, NJ</p>
          <div className="bg-[#12151C] border border-white/5 rounded-xl p-8 max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-2 mb-4">
              <MapPin className="w-5 h-5 text-[#cc3737]" />
              <span className="text-white font-medium">98 Willow Ave, Hoboken, NJ 07030</span>
            </div>
            <p className="text-[#64748B] text-sm mb-6">Delivery radius covers all of Hoboken</p>
            <Link to="/shop" className="inline-flex bg-[#cc3737] hover:bg-[#d94444] text-white rounded-full px-8 py-3 font-medium transition-colors" data-testid="cta-order-btn">
              Start Your Order
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="border-t border-white/5 bg-[#0B0E14] py-12" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="Hoboken LGD" className="w-10 h-10 rounded-lg object-cover" />
                <span className="font-['Playfair_Display'] text-lg font-medium text-white">HOBOKEN LIQUOR GROCERY & DELI</span>
              </div>
              <p className="text-[#64748B] text-sm font-light leading-relaxed">Premium liquor, grocery & deli delivered to your door in Hoboken, NJ.</p>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4 text-sm tracking-wider uppercase">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/shop" className="block text-[#64748B] hover:text-white text-sm font-light transition-colors">Shop All</Link>
                <Link to="/shop?category=Liquor+%26+Spirits" className="block text-[#64748B] hover:text-white text-sm font-light transition-colors">Liquor</Link>
                <Link to="/shop?category=Beer+%26+Wine" className="block text-[#64748B] hover:text-white text-sm font-light transition-colors">Beer & Wine</Link>
                <Link to="/shop?category=Deli+%26+Hot+Food" className="block text-[#64748B] hover:text-white text-sm font-light transition-colors">Deli</Link>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4 text-sm tracking-wider uppercase">Store Info</h4>
              <div className="space-y-2 text-[#64748B] text-sm font-light">
                <p>98 Willow Ave, Hoboken, NJ</p>
                <p>(201) 555-0198</p>
                <p>info@hobokenlgd.com</p>
              </div>
            </div>
            <div>
              <h4 className="text-white font-medium mb-4 text-sm tracking-wider uppercase">Legal</h4>
              <div className="space-y-2">
                <Link to="/terms" className="block text-[#64748B] hover:text-white text-sm font-light transition-colors">Terms of Service</Link>
                <Link to="/privacy" className="block text-[#64748B] hover:text-white text-sm font-light transition-colors">Privacy Policy</Link>
                <Link to="/delivery-policy" className="block text-[#64748B] hover:text-white text-sm font-light transition-colors">Delivery Policy</Link>
              </div>
            </div>
          </div>
          <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#64748B] text-xs font-light">&copy; {new Date().getFullYear()} Hoboken Liquor Grocery & Deli. All rights reserved.</p>
            <p className="text-[#64748B] text-xs font-light">98 Willow Ave, Hoboken, NJ</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
